import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, userManager } from "../auth/auth";
import { Alert, Button, Field, Spinner, Switch, TextInput } from "../components/ui";

function Art() {
  return (
    <div className="auth-art">
      <div className="brand" style={{ padding: 0 }}>
        <img src="/solaros-mark.png" alt="" />
        <div>
          <b>SolarOS</b>
        </div>
      </div>
      <div>
        <h2>
          Instant solar quotes, <span className="gradient-text">straight from your website.</span>
        </h2>
        <p>AI roof designs, priced with your rules and your brand. Your visitors get a proposal in a minute — you get the lead.</p>
      </div>
      <img className="roof" src="/roof-design.webp" alt="A roof with a solar layout designed by SolarOS" />
    </div>
  );
}

export function Login({ signup }: { signup?: boolean }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? "/";
  const [email, setEmail] = useState("owner@sunrise-solar.test");
  const [admin, setAdmin] = useState(false);

  if (auth.ready && auth.signedIn) return <Navigate to={returnTo} replace />;

  return (
    <div className="auth-page">
      <Art />
      <div className="auth-form">
        <div className="auth-card">
          <div>
            <h1>{signup ? "Create your SolarOS account" : "Welcome back"}</h1>
            <p className="muted" style={{ marginTop: 6 }}>
              {signup ? "Start quoting from your website in two minutes. Your first design is free." : "Sign in to your SolarOS dashboard."}
            </p>
          </div>
          {auth.mode === "oidc" ? (
            <div className="stack">
              <Button variant="primary" size="lg" block onClick={() => auth.signIn({ signup, returnTo })} icon={<ArrowRight />}>
                {signup ? "Sign up with email or Google" : "Sign in"}
              </Button>
              {!signup ? (
                <p className="small muted">
                  New to SolarOS?{" "}
                  <button className="link-btn" onClick={() => auth.signIn({ signup: true, returnTo })}>
                    Create an account
                  </button>
                </p>
              ) : (
                <p className="small muted">
                  Already have an account?{" "}
                  <button className="link-btn" onClick={() => auth.signIn({ returnTo })}>
                    Sign in
                  </button>
                </p>
              )}
            </div>
          ) : (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                qc.clear();
                auth.devSignIn({ email: email.trim().toLowerCase(), platformAdmin: admin });
                navigate(returnTo, { replace: true });
              }}
            >
              <Alert tone="warn" title="Development sign-in.">
                The API runs with <code>AUTH_MODE=dev</code>: you are whoever you type. Set <code>VITE_AUTH_AUTHORITY</code> for Entra
                sign-in.
              </Alert>
              <Field label="Email">
                <TextInput type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
              </Field>
              <Switch checked={admin} onChange={setAdmin} label={<span className="row" style={{ gap: 6 }}><ShieldCheck size={16} /> Platform administrator</span>} />
              <Button type="submit" variant="primary" size="lg" block icon={<ArrowRight />}>
                Continue
              </Button>
            </form>
          )}
          <p className="small faint">By continuing you agree to the SolarOS terms and privacy policy.</p>
        </div>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    userManager()
      .signinRedirectCallback()
      .then((user) => {
        const state = user.state as { returnTo?: string } | undefined;
        window.location.replace(state?.returnTo ?? "/");
      })
      .catch((e: Error) => setError(e.message));
  }, [navigate]);
  if (error)
    return (
      <div className="center-fill" style={{ minHeight: "100vh", padding: 16 }}>
        <div className="stack" style={{ maxWidth: 440 }}>
          <Alert tone="danger" title="Sign-in failed.">
            {error}
          </Alert>
          <Button onClick={() => navigate("/login")}>Back to sign in</Button>
        </div>
      </div>
    );
  return <Spinner label="Signing in…" />;
}
